import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pause, Play } from 'lucide-react';
import { subscribe } from '../../hooks/useWebSocket';
import type { EventListItem } from '../../api/types';
import { STREAM_MAX, classifyStreamColor } from './liveMonitor.types';

// 권위: docs/ARCH_DASHBOARD.md §4.5 좌측 패널 — 에이전트 상태 스트림.
// - `/topic/system-events` 를 구독해 prepend (최신이 위).
// - 이벤트 색상 규약은 classifyStreamColor 참조.
// - 일시정지/재개 토글: 폭주 시 이벤트 수신을 버퍼에 쌓지 않고 drop (오래된 것부터).
// - 최대 STREAM_MAX 건 보관.
// - 행 클릭 → /history/:eventId 딥링크 (화면 4 상세 모달).

interface Props {
  /** 테스트 용이성을 위해 초기 아이템 주입 가능. 기본 []. */
  initialItems?: EventListItem[];
}

export function LiveMonitorEventStream({ initialItems = [] }: Props): JSX.Element {
  const navigate = useNavigate();
  const [items, setItems] = useState<EventListItem[]>(initialItems);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(paused);
  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  useEffect(() => {
    const off = subscribe('/topic/system-events', (msg) => {
      if (pausedRef.current) return; // 일시정지 중에는 drop.
      if (!msg.body) return;
      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(msg.body);
      } catch {
        return;
      }
      const item = normalize(parsed);
      if (!item) return;
      setItems((prev) => {
        if (prev.some((p) => p.eventId === item.eventId)) return prev;
        const next = [item, ...prev];
        if (next.length > STREAM_MAX) next.length = STREAM_MAX;
        return next;
      });
    });
    return off;
  }, []);

  const clear = useCallback(() => setItems([]), []);

  return (
    <section className="live-panel" aria-label="에이전트 이벤트 스트림">
      <header className="live-panel__header">
        <h2 className="live-panel__title">에이전트 이벤트</h2>
        <div className="live-panel__actions">
          <span className="syslog-muted">
            {items.length} / {STREAM_MAX}
          </span>
          <button
            type="button"
            className={`live-panel__pause${paused ? ' live-panel__pause--paused' : ''}`}
            onClick={() => setPaused((v) => !v)}
            title={paused ? '재개' : '일시정지'}
            aria-pressed={paused}
          >
            {paused ? <Play size={14} aria-hidden /> : <Pause size={14} aria-hidden />}
            <span>{paused ? '재개' : '일시정지'}</span>
          </button>
          <button
            type="button"
            className="syslog-btn syslog-btn--ghost"
            onClick={clear}
            disabled={items.length === 0}
          >
            비우기
          </button>
        </div>
      </header>

      <div className="live-panel__body">
        {items.length === 0 && (
          <div className="syslog-empty">수신된 이벤트가 없습니다. 대기 중…</div>
        )}
        <ul className="live-stream">
          {items.map((it) => {
            const color = classifyStreamColor(it);
            return (
              <li
                key={it.eventId}
                className={`live-stream__item live-stream__item--${color}`}
              >
                <button
                  type="button"
                  className="live-stream__link"
                  onClick={() => navigate(`/history/${it.eventId}`)}
                >
                  <span className="live-stream__time">{formatTime(it.occurredAt)}</span>
                  <span className={`syslog-badge syslog-badge--${it.severity.toLowerCase()}`}>
                    {it.severity}
                  </span>
                  <span className="live-stream__source">{it.sourceAgent || '-'}</span>
                  <code className="live-stream__code">{it.eventCode}</code>
                  <span className="live-stream__summary" title={it.summary}>
                    {it.summary}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

function normalize(p: Record<string, unknown>): EventListItem | null {
  const eventId =
    (p.eventId as string | undefined) ??
    (p.event_id as string | undefined) ??
    (p.id as string | undefined);
  const severity = ((p.severity as string | undefined) ?? '').toUpperCase();
  if (!eventId || !severity) return null;
  if (!['FATAL', 'ERROR', 'WARN', 'INFO', 'DEBUG'].includes(severity)) return null;
  return {
    eventId,
    occurredAt:
      (p.occurredAt as string | undefined) ??
      (p.occurred_at as string | undefined) ??
      new Date().toISOString(),
    severity: severity as EventListItem['severity'],
    sourceAgent:
      (p.sourceAgent as string | undefined) ?? (p.source_agent as string | undefined) ?? '',
    eventCode:
      (p.eventCode as string | undefined) ?? (p.event_code as string | undefined) ?? '',
    summary: (p.summary as string | undefined) ?? '',
    entityRefs:
      (p.entityRefs as Record<string, unknown> | undefined) ??
      (p.entity_refs as Record<string, unknown> | undefined) ??
      null,
    traceId:
      (p.traceId as string | undefined) ?? (p.trace_id as string | undefined) ?? null,
    acked: false,
  };
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}
